import React, { useState } from 'react';
import { FiSettings, FiChevronDown, FiCamera } from 'react-icons/fi';
import { HiPaintBrush, HiSparkles } from 'react-icons/hi2';
import './BackgroundOptions.css';

/**
 * BackgroundOptions Component
 * Handles background type selection and related inputs
 */
const BackgroundOptions = ({
  backgroundType,
  onBackgroundTypeChange,
  onBackgroundColorChange,
  passportSize,
  onPassportSizeChange,
  customWidth,
  customHeight,
  onCustomWidthChange,
  onCustomHeightChange,
  selectedCountry,
  onCountryChange,
  countriesList
}) => {
  const [colorValue, setColorValue] = useState('#ffffff');

  const handleTypeChange = (type) => {
    onBackgroundTypeChange(type);
  };

  const handleColorChange = (e) => {
    const color = e.target.value;
    setColorValue(color);
    onBackgroundColorChange(color);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
        <FiSettings className="text-blue-600" /> Settings
      </h2>

      {/* Background Type */}
      <div className="space-y-3">
        <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Background Type</p>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => handleTypeChange('transparent')}
            className={`py-3 px-4 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${backgroundType === 'transparent'
              ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-600'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 shadow-sm'
              }`}
          >
            <HiSparkles className="text-xl" />
            <span className="text-sm font-bold">Transparent</span>
          </button>

          <button
            onClick={() => handleTypeChange('color')}
            className={`py-3 px-4 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${backgroundType === 'color'
              ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-600'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 shadow-sm'
              }`}
          >
            <HiPaintBrush className="text-xl" />
            <span className="text-sm font-bold">Solid Color</span>
          </button>
        </div>

        {backgroundType === 'color' && (
          <div className="mt-3 flex items-center gap-3 p-3 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
            <input
              type="color"
              value={colorValue}
              onChange={handleColorChange}
              className="w-10 h-10 rounded-lg cursor-pointer border-none overflow-hidden"
            />
            <input
              type="text"
              value={colorValue}
              onChange={handleColorChange}
              placeholder="#FFFFFF"
              className="flex-1 bg-transparent border-none outline-none focus:ring-0 text-sm font-mono uppercase dark:text-white"
            />
          </div>
        )}
      </div>

      {/* Preset Sizes */}
      <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-gray-700">
        <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Image Size</p>

        {/* Passport Checkbox */}
        <label className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-slate-900/50 cursor-pointer transition-all shadow-sm">
          <input
            type="checkbox"
            checked={passportSize}
            onChange={(e) => {
              onPassportSizeChange(e.target.checked);
              if (e.target.checked) {
                onCustomWidthChange('');
                onCustomHeightChange('');
                onCountryChange('');
              }
            }}
            className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
          />
          <div className="flex-1">
            <p className="text-sm font-bold text-gray-900 dark:text-white">Standard Passport</p>
            <p className="text-xs text-gray-400">Fixed 600x600 pixels (2x2")</p>
          </div>
          <FiCamera className="text-xl text-gray-400" />
        </label>

        {/* Country Select */}
        <div className="space-y-2">
          <label className="text-xs font-bold text-gray-400 px-1">Specific Country Requirements</label>
          <div className="relative">
            <select
              value={selectedCountry}
              onChange={(e) => {
                const country = e.target.value;
                onCountryChange(country);
                if (country) {
                  onPassportSizeChange(false);
                  onCustomWidthChange('');
                  onCustomHeightChange('');
                }
              }}
              className="w-full pl-4 pr-10 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900 text-gray-900 dark:text-white appearance-none focus:ring-2 focus:ring-blue-500 transition-all text-sm outline-none"
            >
              <option value="">-- Select Country --</option>
              {countriesList.map((country) => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-gray-400">
              <FiChevronDown />
            </div>
          </div>
        </div>

        {/* Custom Size */}
        <div className="space-y-2">
          <label className="text-xs font-bold text-gray-400 px-1">Custom Resolution</label>
          <div className="grid grid-cols-2 gap-3">
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 text-xs font-bold">W</span>
              <input
                type="number"
                value={customWidth}
                onChange={(e) => {
                  onCustomWidthChange(e.target.value);
                  if (e.target.value) {
                    onPassportSizeChange(false);
                    onCountryChange('');
                  }
                }}
                placeholder="0"
                className="w-full pl-8 pr-3 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900 text-gray-900 dark:text-white text-sm outline-none shadow-sm"
              />
            </div>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 text-xs font-bold">H</span>
              <input
                type="number"
                value={customHeight}
                onChange={(e) => {
                  onCustomHeightChange(e.target.value);
                  if (e.target.value) {
                    onPassportSizeChange(false);
                    onCountryChange('');
                  }
                }}
                placeholder="0"
                className="w-full pl-8 pr-3 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900 text-gray-900 dark:text-white text-sm outline-none shadow-sm"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackgroundOptions;


